import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchCurrentUser, login } from '../../api/auth.api';
import { LoginForm, GoogleLoginButton, AuthDivider, AuthHeader } from '../../components/auth';
import { getDashboardRoute } from '../../hooks/useAuth';
import storage from '../../utils/storage';
import analiticIcone4 from '../../assets/analytic.png';
import AnalisisIcon from '../../assets/data-analysis.png';
import ellipse1 from '../../assets/Ellipse 1.png';
import featureIcon3 from '../../assets/ix_user-management-settings-filled.png';
import survey from '../../assets/survey.png';

const RightPanel = () => (
  <div className="relative hidden h-full w-full items-center justify-center overflow-hidden bg-teal-700 px-5 py-9 text-white lg:flex">
    <div className="absolute inset-0">
      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none">
        <svg viewBox="0 0 1000 1000" className="w-full h-full">
          <circle cx="900" cy="100" r="400" stroke="white" strokeWidth="1" fill="none" />
          <circle cx="900" cy="100" r="600" stroke="white" strokeWidth="1" fill="none" />
          <circle cx="900" cy="100" r="800" stroke="white" strokeWidth="1" fill="none" />
          <circle cx="200" cy="800" r="100" fill="white" filter="blur(40px)" opacity="0.2" />
          <circle cx="600" cy="400" r="150" fill="white" filter="blur(60px)" opacity="0.1" />
        </svg>
      </div>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.12),transparent_45%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(255,255,255,0.08),transparent_50%)]" />
    </div>

    <div className="relative z-10 flex h-full w-full max-w-xl items-start justify-center px-6 py-1">
      <div className="relative h-[60rem] w-[60rem]">
        <div className="absolute left-[6rem] top-5 flex flex-col items-center text-center">
          <div className="relative flex h-30 w-28 items-center justify-center drop-shadow-[0_18px_32px_rgba(0,0,0,0.26)]">
            <img src={ellipse1} alt="" aria-hidden="true" className="h-full w-full object-contain" />
            <img src={survey} alt="Créer des enquêtes" className="absolute h-16 w-16" />
          </div>
          <p className="mt-3 text-base font-medium text-white">Créer des enquêtes</p>
        </div>

        <div className="absolute left-[2rem] top-[15rem] flex flex-col items-center text-center">
          <div className="relative flex h-33 w-32 items-center justify-center drop-shadow-[0_18px_32px_rgba(0,0,0,0.26)]">
            <img src={ellipse1} alt="" aria-hidden="true" className="h-full w-full object-contain" />
            <img src={featureIcon3} alt="Gérer les utilisateurs" className="absolute h-16 w-16" />
          </div>
          <p className="mt-3 text-base font-medium text-white">Gérer les utilisateurs</p>
        </div>

        <div className="absolute left-[19rem] top-[6rem] flex flex-col items-center text-center">
          <div className="relative flex h-40 w-40 items-center justify-center drop-shadow-[0_18px_32px_rgba(0,0,0,0.26)]">
            <img src={ellipse1} alt="" aria-hidden="true" className="h-full w-full object-contain" />
            <img src={analiticIcone4} alt="Analyser les résultats" className="absolute h-20 w-20 filter brightness-0 invert" />
          </div>
          <p className="mt-3 text-base font-medium text-white">Analyser les résultats</p>
        </div>

        <div className="absolute left-[14.5rem] top-[20rem] flex flex-col items-center text-center">
          <div className="relative flex h-40 w-40 items-center justify-center drop-shadow-[0_18px_32px_rgba(0,0,0,0.26)]">
            <img src={ellipse1} alt="" aria-hidden="true" className="h-full w-full object-contain" />
            <img src={AnalisisIcon} alt="Suivez vos performances" className="absolute h-20 w-20 filter brightness-0 invert" />
          </div>
          <p className="mt-3 text-base font-medium text-white leading-snug text-center">
            Suivez vos performances <br /> en temps réel
          </p>
        </div>
      </div>
    </div>
  </div>
);

const Connexion = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = (values = { email, password }) => {
    const nextErrors = { email: '', password: '' };

    if (!values.email.trim()) {
      nextErrors.email = "L'adresse email est obligatoire.";
    } else if (!emailRegex.test(values.email.trim())) {
      nextErrors.email = "Format d'email invalide.";
    }

    if (!values.password.trim()) {
      nextErrors.password = 'Le mot de passe est obligatoire.';
    }

    return nextErrors;
  };

  const updateField = (field, value) => {
    if (field === 'email') {
      setEmail(value);
    } else {
      setPassword(value);
    }

    setErrors((prev) => {
      const updated = { ...prev };
      const validation = validate({
        email: field === 'email' ? value : email,
        password: field === 'password' ? value : password,
      });
      updated[field] = validation[field];
      return updated;
    });
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, ...validate() }));
  };

  const handleSubmit = async () => {
    const validation = validate();
    const hasErrors = Object.values(validation).some(Boolean);

    if (hasErrors) {
      setErrors(validation);
      setTouched({ email: true, password: true });
      return;
    }

    setIsLoading(true);
    try {
      const data = await login({ email, password });
      const { access, refresh, user } = data || {};

      if (access && refresh) {
        try {
          storage.setToken(access, refresh);

          let userInfo = user;
          if (!userInfo) {
            try {
              const decoded = storage.decodeToken();
              userInfo = decoded;
            } catch (decodeError) {
              // Silently fail
            }
          }

          if (!userInfo || !userInfo.role) {
            try {
              const me = await fetchCurrentUser();
              if (me) {
                userInfo = me;
              }
            } catch (meError) {
              // Silently fail
            }
          }

          if (!userInfo || !userInfo.role) {
            toast.error('Impossible de déterminer le rôle utilisateur.');
            storage.clearAll();
            setIsLoading(false);
            return;
          }

          const existingRole = storage.getRole();
          if (existingRole && existingRole !== userInfo.role) {
            toast.error(
              'Une autre session active existe avec un rôle différent. Pour des raisons de sécurité, vous devez d\'abord vous déconnecter.'
            );
            storage.clearAll();
            setIsLoading(false);
            return;
          }

          storage.setUser(userInfo);
          const dashboardRoute = getDashboardRoute(userInfo.role);
          toast.success('Connexion réussie ! Redirection...');
          setTimeout(() => navigate(dashboardRoute), 1500);
        } catch (storageError) {
          toast.error('Erreur de sécurité: impossible de stocker la session.');
          setIsLoading(false);
        }
      } else {
        toast.error('Identifiants invalides ou réponse invalide.');
      }
    } catch (error) {
      const inlineMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.non_field_errors?.[0] ||
        error?.message ||
        'Identifiants invalides. Merci de réessayer.';
      toast.error(inlineMessage);
      setTouched({ email: true, password: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_1fr]">
      <div className="absolute inset-0 z-0 opacity-[0.07] pointer-events-none">
        <svg width="100%" height="100%">
          <pattern
            id="arrow-pattern"
            x="0"
            y="0"
            width="120"
            height="120"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(-15)"
          >
            <path
              d="M10 50 L40 20 M40 20 L25 20 M40 20 L40 35"
              stroke="#0d9488"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M70 100 L100 70 M100 70 L85 70 M100 70 L100 85"
              stroke="#0d9488"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </pattern>
          <rect width="100%" height="100%" fill="url(#arrow-pattern)" />
        </svg>
      </div>

      <div className="flex flex-col items-center bg-white px-6 py-10 sm:px-12 md:px-16 lg:justify-center overflow-y-auto scrollbar-hide">
        <div className="relative z-10 flex flex-col items-center gap-8 w-full max-w-md py-4">
          <AuthHeader title="Connexion" subtitle="Accédez à votre tableau de bord" />

          <LoginForm
            email={email}
            password={password}
            errors={errors}
            touched={touched}
            isLoading={isLoading}
            onEmailChange={(value) => updateField('email', value)}
            onPasswordChange={(value) => updateField('password', value)}
            onEmailBlur={() => handleBlur('email')}
            onPasswordBlur={() => handleBlur('password')}
            onSubmit={handleSubmit}
          />

          <AuthDivider text="Ou" />

          <GoogleLoginButton />
        </div>
      </div>

      <RightPanel />
    </div>
  );
};

export default Connexion;
